import { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { ConfirmDialog } from "../ConfirmDialog";
import { DEFAULT_CONTENT, slugify } from "../../data/defaults";

// ---- Alan tanımları ---------------------------------------------------------

const GROUPS = [
  {
    id: "genel",
    title: "Genel & İletişim Bilgileri",
    desc: "Telefon, adres, sosyal medya ve tüm sayfalarda görünen ortak metinler.",
    fields: [
      { key: "site_phone", label: "Telefon Numarası" },
      { key: "site_whatsapp", label: "WhatsApp Numarası (905...)" },
      { key: "site_address", label: "Adres" },
      { key: "site_address_city", label: "Adres — İlçe / İl" },
      { key: "site_city", label: "Şehir Etiketi", ph: "Atakum / Samsun" },
      { key: "site_since", label: "Kuruluş Yılı" },
      { key: "site_instagram", label: "Instagram Linki" },
      { key: "map_address", label: "Harita Adresi (Google Maps sorgusu)", area: true },
      { key: "map_query", label: "Haritada Aranacak İşletme Adı" },
      { key: "map_business_url", label: "Google Business / Yol Tarifi Linki", area: true },
      { key: "footer_text", label: "Footer Tanıtım Metni", area: true },
      { key: "pagehero_label", label: "Sayfa Başlığı Üstü Etiket", ph: "Yalı Yurt — Erkek Öğrenci Yurdu" },
      { key: "marquee_items", label: "Kayan Şerit Öğeleri (virgülle ayırın)", area: true },
    ],
  },
  {
    id: "anasayfa",
    title: "Ana Sayfa",
    desc: "Hero, tanıtım, istatistikler, değerler ve bölüm başlıkları.",
    fields: [
      { key: "hero_label", label: "Hero — Üst Etiket" },
      { key: "hero_title_1", label: "Hero Başlık — 1. Satır" },
      { key: "hero_title_2", label: "Hero Başlık — 2. Satır" },
      { key: "hero_title_3", label: "Hero Başlık — 3. Satır" },
      { key: "hero_sub", label: "Hero Alt Açıklama", area: true },
      { key: "intro_label", label: "Tanıtım — Etiket" },
      { key: "intro_title", label: "Tanıtım — Başlık" },
      { key: "intro_text", label: "Tanıtım — Metin", area: true },
      { key: "intro_link", label: "Tanıtım — Link Yazısı" },
      { key: "values_label", label: "Değerler — Etiket" },
      { key: "values_title", label: "Değerler — Başlık" },
      { key: "values_desc", label: "Değerler — Açıklama", area: true },
      { key: "rooms_label", label: "Odalar Önizleme — Etiket" },
      { key: "rooms_title", label: "Odalar Önizleme — Başlık" },
      { key: "rooms_desc", label: "Odalar Önizleme — Açıklama", area: true },
      { key: "services_label", label: "İmkanlar Önizleme — Etiket" },
      { key: "services_title", label: "İmkanlar Önizleme — Başlık" },
      { key: "services_desc", label: "İmkanlar Önizleme — Açıklama", area: true },
      { key: "gallery_label", label: "Galeri Önizleme — Etiket" },
      { key: "gallery_title", label: "Galeri Önizleme — Başlık" },
      { key: "video_label", label: "Video Bölümü — Etiket" },
      { key: "video_title", label: "Video Bölümü — Başlık" },
      { key: "video_desc", label: "Video Bölümü — Açıklama", area: true },
      { key: "location_label", label: "Konum Önizleme — Etiket" },
      { key: "location_title", label: "Konum Önizleme — Başlık (satır için Enter)", area: true },
      { key: "location_desc", label: "Konum Önizleme — Açıklama", area: true },
      { key: "contact_teaser_title", label: "İletişim Teaser — Başlık" },
      { key: "contact_teaser_text", label: "İletişim Teaser — Açıklama", area: true },
      { key: "photo_break_quote", label: "Fotoğraf Bandı Cümlesi" },
      { key: "cta_label", label: "Final CTA — Etiket" },
      { key: "cta_title", label: "Final CTA — Başlık (satır için Enter)", area: true },
      { key: "cta_text", label: "Final CTA — Açıklama", area: true },
    ],
    lists: [
      { key: "stats", label: "İstatistik Bandı", itemName: "istatistik", itemFields: [{ key: "value", label: "Değer" }, { key: "label", label: "Açıklama" }] },
      { key: "values", label: "Değerler (Neden Yalı Yurt)", itemName: "değer", autoNo: true, itemFields: [{ key: "title", label: "Başlık" }, { key: "desc", label: "Açıklama", area: true }] },
      { key: "home_distances", label: "Konum Önizleme Mesafeleri", itemName: "mesafe", itemFields: [{ key: "place", label: "Yer" }, { key: "time", label: "Süre" }] },
    ],
  },
  {
    id: "odalar",
    title: "Odalar Sayfası",
    desc: "Oda tiplerini düzenleyin, yeni oda ekleyin veya kaldırın. Oda fotoğrafları Görseller sekmesinden yönetilir.",
    fields: [
      { key: "ph_odalar_title", label: "Sayfa Başlığı" },
      { key: "ph_odalar_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_odalar_desc", label: "Sayfa Açıklaması", area: true },
    ],
    lists: [
      { key: "rooms", label: "Oda Tipleri", itemName: "oda", itemFields: [
        { key: "name", label: "Oda Adı" },
        { key: "capacity", label: "Kapasite", ph: "1 Kişi" },
        { key: "short", label: "Kısa Açıklama (kart üzeri)", area: true },
        { key: "detail", label: "Detaylı Açıklama (oda sayfası)", area: true },
        { key: "features", label: "Özellikler (her satıra bir tane)", list: true },
      ] },
      { key: "odalar_badges", label: "Alt Bilgi Kutuları", itemName: "kutu", itemFields: [{ key: "label", label: "Etiket" }, { key: "text", label: "Metin" }] },
    ],
  },
  {
    id: "hizmetler",
    title: "İmkanlar Sayfası",
    desc: "Hizmet kartları ana sayfada da listelenir.",
    fields: [
      { key: "ph_hizmetler_title", label: "Sayfa Başlığı" },
      { key: "ph_hizmetler_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_hizmetler_desc", label: "Sayfa Açıklaması", area: true },
      { key: "hizmet_yemek_label", label: "Yemek Bölümü — Etiket" },
      { key: "hizmet_yemek_title", label: "Yemek Bölümü — Başlık" },
      { key: "hizmet_yemek_text", label: "Yemek Bölümü — Metin", area: true },
      { key: "hizmet_sosyal_label", label: "Sosyal Alanlar — Etiket" },
      { key: "hizmet_sosyal_text", label: "Sosyal Alanlar — Metin", area: true },
    ],
    lists: [
      { key: "services", label: "Hizmet Kartları", itemName: "hizmet", autoNo: true, itemFields: [{ key: "title", label: "Başlık" }, { key: "desc", label: "Açıklama", area: true }] },
    ],
  },
  {
    id: "galeri",
    title: "Galeri Sayfası",
    desc: "Galeri fotoğrafları Görseller sekmesinden yönetilir.",
    fields: [
      { key: "ph_galeri_title", label: "Sayfa Başlığı" },
      { key: "ph_galeri_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_galeri_desc", label: "Sayfa Açıklaması", area: true },
    ],
  },
  {
    id: "videolar",
    title: "Videolar Sayfası",
    desc: "YouTube veya Instagram gönderi/Reel linki eklerseniz video sitede oynatılır; boş bırakırsanız 'Yakında' notu görünür.",
    fields: [
      { key: "ph_videolar_title", label: "Sayfa Başlığı" },
      { key: "ph_videolar_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_videolar_desc", label: "Sayfa Açıklaması", area: true },
      { key: "video_soon_text", label: "'Yakında' Pencere Metni", area: true },
    ],
    lists: [
      { key: "videos", label: "Videolar", itemName: "video", itemFields: [{ key: "title", label: "Video Başlığı" }, { key: "url", label: "YouTube / Instagram Linki (opsiyonel)", ph: "https://www.youtube.com/watch?v=... veya instagram.com/p/..." }] },
    ],
  },
  {
    id: "konum",
    title: "Konum Sayfası",
    fields: [
      { key: "ph_konum_title", label: "Sayfa Başlığı" },
      { key: "ph_konum_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_konum_desc", label: "Sayfa Açıklaması", area: true },
      { key: "konum_dist_title", label: "Mesafeler — Başlık" },
      { key: "konum_dist_desc", label: "Mesafeler — Açıklama", area: true },
    ],
    lists: [
      { key: "distances", label: "Mesafe Kartları", itemName: "mesafe", itemFields: [{ key: "place", label: "Yer" }, { key: "time", label: "Süre / Mesafe" }, { key: "note", label: "Not (opsiyonel)" }] },
    ],
  },
  {
    id: "hakkimizda",
    title: "Hakkımızda Sayfası",
    fields: [
      { key: "ph_hakkimizda_title", label: "Sayfa Başlığı" },
      { key: "ph_hakkimizda_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_hakkimizda_desc", label: "Sayfa Açıklaması", area: true },
      { key: "about_label", label: "Hikaye — Etiket" },
      { key: "about_title", label: "Hikaye — Başlık" },
      { key: "about_p1", label: "Hikaye — 1. Paragraf", area: true },
      { key: "about_p2", label: "Hikaye — 2. Paragraf", area: true },
      { key: "about_values_title", label: "Değerler Bölümü — Başlık" },
    ],
    lists: [
      { key: "milestones", label: "Değerler / Kilometre Taşları", itemName: "madde", autoNo: true, itemFields: [{ key: "title", label: "Başlık" }, { key: "desc", label: "Açıklama", area: true }] },
    ],
  },
  {
    id: "iletisim",
    title: "İletişim Sayfası",
    fields: [
      { key: "ph_iletisim_title", label: "Sayfa Başlığı" },
      { key: "ph_iletisim_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_iletisim_desc", label: "Sayfa Açıklaması", area: true },
      { key: "contact_form_title", label: "İletişim Formu — Başlık" },
      { key: "contact_form_text", label: "İletişim Formu — Açıklama", area: true },
      { key: "randevu_title", label: "Randevu Formu — Başlık" },
      { key: "randevu_text", label: "Randevu Formu — Açıklama", area: true },
      { key: "time_slots", label: "Randevu Saat Aralıkları (virgülle ayırın)", area: true },
    ],
  },
  {
    id: "blog",
    title: "Blog Sayfası",
    desc: "Blog yazıları Blog sekmesinden yönetilir.",
    fields: [
      { key: "ph_blog_title", label: "Sayfa Başlığı" },
      { key: "ph_blog_subtitle", label: "Sayfa Alt Başlığı" },
      { key: "ph_blog_desc", label: "Sayfa Açıklaması", area: true },
    ],
  },
];

// ---- Yardımcı bileşenler ----------------------------------------------------

const Field = ({ f, value, onChange }) => (
  <div>
    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">{f.label}</label>
    {f.area ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="input-field resize-y" placeholder={f.ph || ""} data-testid={`content-${f.key}`} />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="input-field" placeholder={f.ph || ""} data-testid={`content-${f.key}`} />
    )}
  </div>
);

const ListEditor = ({ list, items, onChange }) => {
  const pad = (i) => String(i + 1).padStart(2, "0");

  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => {
    const empty = {};
    list.itemFields.forEach((f) => { empty[f.key] = f.list ? [] : ""; });
    onChange([...items, empty]);
  };

  return (
    <div className="border border-line bg-cream-100 p-5" data-testid={`list-${list.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-800">{list.label}</p>
        <button type="button" onClick={add} data-testid={`list-add-${list.key}`}
          className="flex items-center gap-1.5 border border-ink-900 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-ink-900 hover:text-cream-50">
          <Plus size={13} /> Ekle
        </button>
      </div>
      <div className="mt-4 space-y-4">
        {items.map((it, i) => (
          <div key={i} className="border border-line bg-white p-4" data-testid={`list-item-${list.key}-${i}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink-400">
                {list.itemName} {list.autoNo ? pad(i) : i + 1}
              </span>
              <button type="button" onClick={() => remove(i)} data-testid={`list-remove-${list.key}-${i}`}
                aria-label="Sil" className="text-ink-400 transition-colors hover:text-red-600">
                <Trash2 size={15} />
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {list.itemFields.map((f) => (
                <div key={f.key} className={f.area || f.list ? "sm:col-span-2" : ""}>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">{f.label}</label>
                  {f.area || f.list ? (
                    <textarea rows={f.list ? 4 : 2}
                      value={f.list ? (Array.isArray(it[f.key]) ? it[f.key].join("\n") : it[f.key] || "") : it[f.key] || ""}
                      onChange={(e) => update(i, f.key, f.list
                        ? e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                        : e.target.value)}
                      className="input-field resize-y text-sm" placeholder={f.ph || ""}
                      data-testid={`list-input-${list.key}-${i}-${f.key}`} />
                  ) : (
                    <input value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)}
                      className="input-field text-sm" placeholder={f.ph || ""}
                      data-testid={`list-input-${list.key}-${i}-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
            {list.key === "rooms" && (
              <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-ink-400">
                Adres: /odalar/{it.slug || slugify(it.name)} — fotoğraf için Görseller sekmesindeki oda slotunu kullanın
              </p>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-ink-400">Henüz öğe yok. "Ekle" ile başlayın.</p>
        )}
      </div>
    </div>
  );
};

// ---- Ana sekme ---------------------------------------------------------------

const IcerikTab = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openGroup, setOpenGroup] = useState("genel");
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    api.get("/content")
      .then(({ data: d }) => setData({ ...DEFAULT_CONTENT, ...(d.data || {}) }))
      .catch(() => setData({ ...DEFAULT_CONTENT }));
  }, []);

  if (!data) return <p className="text-sm text-ink-500">Yükleniyor...</p>;

  const setKey = (key, val) => setData((prev) => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      // autoNo listelerde numaraları yeniden üret
      ["values", "services", "milestones"].forEach((k) => {
        if (Array.isArray(payload[k])) {
          payload[k] = payload[k].map((it, i) => ({ ...it, no: String(i + 1).padStart(2, "0") }));
        }
      });
      // oda slug'larını üret
      if (Array.isArray(payload.rooms)) {
        const seen = new Set();
        payload.rooms = payload.rooms.map((r) => {
          let slug = r.slug || slugify(r.name);
          while (seen.has(slug)) slug = `${slug}-2`;
          seen.add(slug);
          return { ...r, slug };
        });
      }
      await api.put("/admin/content", { data: payload });
      setData(payload);
      toast.success("İçerik kaydedildi — sitede anında güncellendi");
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setConfirm({
      message: "Tüm içerik düzenlemeleri silinsin ve site varsayılan metinlere mi dönsün?",
      confirmLabel: "Evet, Sıfırla",
      action: async () => {
        try {
          await api.put("/admin/content", { data: {} });
          setData({ ...DEFAULT_CONTENT });
          toast.success("Tüm içerik varsayılanlara döndürüldü");
        } catch {
          toast.error("Sıfırlanamadı");
        }
      },
    });
  };

  return (
    <div data-testid="icerik-tab" className="max-w-3xl">
      <h1 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">İçerik Yönetimi</h1>
      <p className="mt-2 text-sm text-ink-500">
        Sitedeki tüm sayfaların başlık, açıklama ve liste içeriklerini buradan düzenleyin. Kaydettikten sonra değişiklikler sitede anında görünür.
      </p>

      <div className="mt-8 space-y-4">
        {GROUPS.map((g) => {
          const open = openGroup === g.id;
          return (
            <div key={g.id} className="border border-line bg-white" data-testid={`group-${g.id}`}>
              <button type="button" onClick={() => setOpenGroup(open ? null : g.id)}
                data-testid={`group-toggle-${g.id}`}
                className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-cream-100">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.14em] text-ink-900">{g.title}</p>
                  {g.desc && <p className="mt-1 text-xs normal-case tracking-normal text-ink-400">{g.desc}</p>}
                </div>
                <ChevronDown size={17} className={`shrink-0 text-ink-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="space-y-5 border-t border-line px-6 py-6">
                  {g.fields?.map((f) => (
                    <Field key={f.key} f={f} value={data[f.key]} onChange={(v) => setKey(f.key, v)} />
                  ))}
                  {g.lists?.map((l) => (
                    <ListEditor key={l.key} list={l} items={data[l.key] || []} onChange={(v) => setKey(l.key, v)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="content-save">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
        <button onClick={resetAll} data-testid="content-reset"
          className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-400 underline-offset-4 transition-colors hover:text-red-600 hover:underline">
          Tümünü Varsayılana Dön
        </button>
      </div>

      <ConfirmDialog
        open={!!confirm}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const action = confirm?.action;
          setConfirm(null);
          if (action) await action();
        }}
      />
    </div>
  );
};

export default IcerikTab;
